/* eslint-disable react-refresh/only-export-components */
import React, { FC, ReactNode } from "react";

import {
  AI_DARK_GRADIENT,
  AI_ICON_GRADIENT,
  AI_ICON_GRADIENT_STOPS,
  AI_SHINE_BAND,
  AI_STROKE_GRADIENT,
  aiSurfaceWash,
  IgAmberPrimitives,
  IgAzurePrimitives,
  IgBasePrimitives,
  IgBluePrimitives,
  IgBorderColors,
  IgCoralPrimitives,
  IgCoreBackgroundColors,
  IgCoreTextColors,
  IgCyanPrimitives,
  IgFontFamily,
  IgGrayPrimitives,
  IgGreenPrimitives,
  IgIconColors,
  IgIndigoPrimitives,
  IgInteractiveBackgroundColors,
  IgLimePrimitives,
  IgOrangePrimitives,
  IgPinkPrimitives,
  IgPurplePrimitives,
  IgRedPrimitives,
  IgRosePrimitives,
  IgShadows,
  IgSlatePrimitives,
  IgTealPrimitives,
  IgVioletPrimitives,
  IgYellowPrimitives,
} from "@intelligems/ig-design-tokens/constants";
import {
  createTheme,
  keyframes,
  PaletteMode,
  SxProps,
  ThemeProvider,
} from "@mui/material";
import { Theme } from "@mui/material/styles";

import "./types";

export type Modify<T, R> = Omit<T, keyof R> & R;

/** Drives the shine band's sweep; `translateX` keeps it on the compositor. */
const AI_SHINE_SWEEP = keyframes`
  to {
    transform: translateX(100%);
  }
`;

export function createCustomTheme(mode?: PaletteMode): Theme {
  const baseTheme = createTheme({
    palette: {
      // NOT TO BE USED
      primary: {
        main: IgCoreTextColors.primary,
        light: IgCoreTextColors.primary,
        dark: IgCoreTextColors.primary,
      },
      // NOT TO BE USED
      secondary: {
        main: IgCoreTextColors.secondary,
        light: IgCoreTextColors.secondary,
        dark: IgCoreTextColors.secondary,
      },
      // NOT TO BE USED
      error: {
        main: IgCoreTextColors.tonal.error,
        light: IgCoreTextColors.tonal.error,
        dark: IgCoreTextColors.tonal.error,
      },
      // NOT TO BE USED
      warning: {
        main: IgCoreTextColors.tonal.warning,
        light: IgCoreTextColors.tonal.warning,
        dark: IgCoreTextColors.tonal.warning,
      },
      // NOT TO BE USED
      info: {
        main: IgCoreTextColors.tonal.info,
        light: IgCoreTextColors.tonal.info,
        dark: IgCoreTextColors.tonal.info,
      },
      // NOT TO BE USED
      success: {
        main: IgCoreTextColors.tonal.success,
        light: IgCoreTextColors.tonal.success,
        dark: IgCoreTextColors.tonal.success,
      },
      grey: IgGrayPrimitives,
      common: {
        black: "#1E1E1E",
      },
    },
    spacing: 4,
    // Every Body and Caps style sits on a 1.5 line: 14/21, 12/18, 10/15.
    typography: {
      body1: {
        fontSize: "14px",
        lineHeight: "21px",
        fontFamily: IgFontFamily.body,
      },
      body2: {
        lineHeight: "150%",
        fontSize: "12px",
        fontFamily: IgFontFamily.body,
      },
      caption: {
        lineHeight: 1.5,
        fontFamily: IgFontFamily.body,
      },
      overline: {
        lineHeight: 1.5,
        fontFamily: IgFontFamily.body,
      },
      subtitle2: {
        lineHeight: "18px",
        letterSpacing: "0.25px",
        fontFamily: IgFontFamily.body,
      },
      h1: {
        lineHeight: "30px",
        fontSize: "22px",
        fontFamily: IgFontFamily.heading,
      },
      h2: {
        lineHeight: "26px",
        fontSize: "18px",
        fontFamily: IgFontFamily.heading,
      },
      h3: {
        lineHeight: "24px",
        fontSize: "16px",
        fontFamily: IgFontFamily.heading,
      },
      allVariants: {
        color: IgCoreTextColors.primary,
        fontWeight: 400,
      },
      fontFamily: IgFontFamily.body,
      fontWeightMedium: 600,
      fontWeightBold: 700,
    },
    components: {
      MuiList: {
        styleOverrides: {
          root: {
            paddingTop: "0px",
            paddingBottom: "0px",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "6px",
            variants: [
              {
                // AI surface — the iridescent-framed card used by AI affordances.
                // Consumed via `<IgPaper variant="ai" />`.
                props: { variant: "ai" },
                style: {
                  borderRadius: "8px",
                  boxShadow: "none",
                  padding: "30px 40px",
                  border: "1px solid transparent",
                  position: "relative",
                  overflow: "hidden",
                },
              },
            ],
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          size: "small",
          color: "secondary",
        },
        styleOverrides: {
          root: {
            borderRadius: "6px",
            backgroundColor: IgCoreBackgroundColors.baseLightHigh,
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "6px",
              border: `1px solid ${IgBorderColors.core.default}`,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: IgBorderColors.accent.active,
            },
            "& .MuiInputBase-root.Mui-disabled": {
              "& > fieldset": {
                borderColor: IgBorderColors.core.disabled,
              },
            },
            "& .MuiSvgIcon-root": {
              color: IgIconColors.light,
              "&.Mui-disabled": {
                color: IgCoreTextColors.placeholder,
              },
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            boxShadow: `${IgShadows.low} !important`,
            borderRadius: "6px",
            border: `1px solid ${IgBorderColors.core.default}`,
            padding: "4px",
            backgroundColor: IgCoreBackgroundColors.baseLightLow,
          },
          popper: {
            borderRadius: "6px",
            textDecoration: "none",
            textTransform: "none",
            backgroundColor: IgCoreBackgroundColors.interactive.neutral.default,
          },
        },
      },
      MuiAccordion: {
        defaultProps: {
          elevation: 0,
          disableGutters: true,
          slotProps: { transition: { unmountOnExit: true } },
        },
        styleOverrides: {
          root: {
            backgroundColor: IgCoreBackgroundColors.baseLightHigh,
            border: `1px solid ${IgBorderColors.core.default}`,
            borderRadius: "8px !important",
            padding: "12px 16px 16px 16px",
            "&:hover": {
              borderShadow: IgShadows.low,
              border: `1px solid ${IgBorderColors.core.hover}`,
            },
            "&.MuiAccordionSummary-content": {
              margin: "0px 0px",
              marginBottom: "8px",
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { variant: "rounded" },
                style: { borderRadius: "8px", width: "32px", height: "32px" },
              },
            ],
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: "small",
        },
      },
      MuiInput: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          input: {
            "&::placeholder": {
              fontSize: "14px",
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            boxShadow: `${IgShadows.low} !important`,
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { color: "accent" },
                style: {
                  backgroundColor:
                    IgCoreBackgroundColors.interactive.accent.default,
                },
              },
              {
                props: { color: "accentDisabled" },
                style: {
                  backgroundColor:
                    IgCoreBackgroundColors.interactive.accent.disabled,
                },
              },
              {
                props: { color: "default" },
                style: {
                  backgroundColor: IgBorderColors.core.default,
                },
              },
            ],
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { color: "accent" },
                style: { color: IgIconColors.accent },
              },
            ],
          },
          colorInfo: {
            backgroundColor: "#EEEEEE !important",
            color: "rgba(0,0,0,.38)",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: `${IgShadows.low} !important`,
            borderRadius: "6px",
            border: `1px solid ${IgBorderColors.core.default}`,
            padding: "4px",
            backgroundColor: IgCoreBackgroundColors.baseLightLow,
          },
          list: {
            borderRadius: "6px",
            textDecoration: "none",
            textTransform: "none",
            backgroundColor: IgCoreBackgroundColors.interactive.neutral.default,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: IgCoreBackgroundColors.interactive.neutral.hover,
            },
            "&.Mui-selected": {
              backgroundColor: "#F1F1F1 !important",
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          root: ({ theme }) => ({
            [theme.breakpoints.up("md")]: {
              marginLeft: "190px",
            },
          }),
        },
      },
      MuiChip: {
        defaultProps: { variant: "outlined", size: "small" },
        styleOverrides: {
          root: {
            variants: [
              {
                props: {
                  color: "accent",
                },
                style: {
                  backgroundColor: IgCoreBackgroundColors.accent,
                },
              },
              {
                props: {
                  color: "info",
                },
                style: {
                  backgroundColor: IgCoreBackgroundColors.tonal.info,
                },
              },
              {
                props: {
                  color: "warning",
                },
                style: {
                  backgroundColor: IgCoreBackgroundColors.tonal.warning,
                },
              },
              {
                props: {
                  color: "error",
                },
                style: {
                  backgroundColor: IgCoreBackgroundColors.tonal.error,
                },
              },
              {
                props: {
                  color: "success",
                },
                style: {
                  backgroundColor: IgCoreBackgroundColors.tonal.success,
                },
              },
              {
                props: ({ color }) =>
                  color === "default" ||
                  color === "primary" ||
                  color === "secondary",
                style: {
                  backgroundColor: IgGrayPrimitives[400],
                },
              },
              {
                props: {
                  color: "purple",
                },
                style: {
                  backgroundColor: IgPurplePrimitives[100],
                },
              },
              {
                props: {
                  size: "small",
                },
                style: {
                  fontSize: "12px",
                  color: IgCoreTextColors.primary,
                  fontWeight: 400,
                  height: "24px !important",
                  borderRadius: "16px",
                },
              },
              {
                props: {
                  size: "medium",
                },
                style: {
                  color: IgCoreTextColors.primary,
                  fontWeight: 600,
                  height: "32px !important",
                  borderRadius: "16px",
                  fontSize: "12px",
                },
              },
              {
                props: ({ icon }) => !!icon,
                style: {
                  paddingLeft: "8px",
                },
              },
            ],
            border: "none",
            fontSize: "14px",
            lineHeight: "16px",
          },
        },
      },
      MuiIconButton: {
        defaultProps: {
          color: "default",
        },
        styleOverrides: {
          root: {
            borderRadius: "6px",
            padding: "8px",
            "& .MuiTouchRipple-root .MuiTouchRipple-child": {
              borderRadius: "6px",
            },
            variants: [
              {
                props: {
                  color: "default",
                },
                style: {
                  color: IgIconColors.light,
                },
              },
            ],
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "none",
            "& .Mui-selected": {
              backgroundColor: IgInteractiveBackgroundColors.neutral.active,
            },
            "&.MuiToggleButton-root:not(.Mui-selected)": {
              color: IgCoreTextColors.tertiary,
            },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: 99999,
            height: "28px",
            variants: [
              {
                props: {
                  size: "large",
                },
                style: {
                  height: "40px",

                  "& .MuiToggleButton-root": {
                    padding: "12px 22px",
                  },
                },
              },
            ],
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: mode === "dark" ? IgIconColors.dark : IgIconColors.light,
            variants: [
              {
                props: { color: "accent" },
                style: {
                  color: IgIconColors.accent,
                },
              },
              {
                props: { color: "dark" },
                style: { color: IgIconColors.dark },
              },
              {
                props: { color: "light" },
                style: { color: IgIconColors.light },
              },
              {
                props: { color: "success" },
                style: { color: IgIconColors.success },
              },
              {
                props: { color: "primary" },
                style: { color: IgIconColors.light },
              },
              {
                props: { color: "info" },
                style: { color: IgIconColors.info },
              },
              {
                props: { color: "warning" },
                style: { color: IgIconColors.warning },
              },
              {
                props: { color: "error" },
                style: { color: IgIconColors.error },
              },
            ],
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          root: {
            // "& .MuiSvgIcon-root": {
            //   color: IgBorderColors.core.default,
            // },
            ...(mode === "dark" && {
              "& .MuiSvgIcon-root": {
                color: IgIconColors.light,
              },
            }),
            "&.MuiButtonBase-root.MuiCheckbox-root": {
              borderRadius: "6px !important",
              height: "32px !important",
              width: "32px !important",
            },
            "& .MuiTouchRipple-root .MuiTouchRipple-child": {
              borderRadius: "6px",
            },
            "&.Mui-disabled": {
              "& .MuiSvgIcon-root": {
                color: IgBorderColors.core.default,
              },
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            "& .MuiSvgIcon-root": { color: IgIconColors.light },
            padding: "8px !important",
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: "2px",
          },
          colorPrimary: {
            backgroundColor: IgBasePrimitives[100],
          },
          bar: {
            backgroundColor: IgBasePrimitives[400],
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            backgroundColor: IgCoreBackgroundColors.baseLightHigh,
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "6px",
              border: `1px solid ${IgBorderColors.core.default}`,
            },
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused:not(.Mui-error) fieldset": {
                borderColor: IgBorderColors.accent.active,
              },
            },
            "& .MuiInputBase-root.Mui-disabled": {
              "& > fieldset": {
                borderColor: IgBorderColors.core.disabled,
              },
            },
            variants: [
              {
                // Denser than `small` for tight surfaces (panel toolbars,
                // slide-overs). MUI has no built-in styling for a custom size,
                // so the input padding `small` would have supplied is restated
                // here alongside the smaller box.
                props: { size: "extraSmall" },
                style: {
                  "& .MuiOutlinedInput-root": {
                    height: "32px",
                    borderRadius: "6px",
                    fontSize: "12px",
                  },
                  "& .MuiOutlinedInput-input": {
                    paddingTop: "8.5px",
                    paddingBottom: "8.5px",
                  },
                },
              },
              {
                // Matches the 40px `large` button, for a field that sits in a
                // row of them (table toolbars). As with `extraSmall`, MUI
                // treats an unknown size as `medium`, so the `small` input
                // padding is restated around the taller box.
                props: { size: "large" },
                style: {
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                  },
                  "& .MuiOutlinedInput-input": {
                    paddingTop: "8.5px",
                    paddingBottom: "8.5px",
                  },
                },
              },
            ],
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { color: "tertiary" },
                style: { color: IgCoreTextColors.tertiary },
              },
              {
                props: { color: "link" },
                style: { color: IgCoreTextColors.accent },
              },
              {
                props: { color: "placeholder" },
                style: { color: IgCoreTextColors.placeholder },
              },
              {
                props: { color: "dark" },
                style: { color: IgCoreTextColors.dark },
              },
              {
                props: { color: "igSecondary" },
                style: {
                  color: IgCoreTextColors.secondary,
                },
              },
            ],
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            // Body 1 on a 1.2 line — the one Body style not on 1.5.
            lineHeight: 1.2,
            "&.Mui-selected": {
              fontWeight: 600,
            },
            textTransform: "none",
            "&.Mui-disabled": {
              color: IgCoreTextColors.interactive.neutral.disabled,
              fontWeight: 400,
            },
            "&:hover": {
              backgroundColor: IgCoreBackgroundColors.interactive.neutral.hover,
              borderTopRightRadius: "6px",
              borderTopLeftRadius: "6px",
            },
          },
          textColorPrimary: {
            color: IgCoreTextColors.interactive.neutral.default,
            fontWeight: 400,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: IgBorderColors.accent.active,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          // The design system's button: 6px radius, 16px sides, a 21px label
          // line, 36px tall at the default size and 40px at `large`. Figma
          // draws the 8px vertical padding inside its 1px stroke and around a
          // 20px label box, so the CSS padding is whatever lands the 21px line
          // on those heights: 7.5px filled, 6.5px under a 1px border.
          root: {
            minHeight: "36px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            lineHeight: "21px",
            borderRadius: "6px",
            padding: "7.5px 16px",
            "& .MuiButton-startIcon": { marginLeft: 0 },
            "& .MuiButton-endIcon": { marginRight: 0 },
            "& .MuiButton-icon": { color: IgIconColors.light },
            "&.Mui-disabled .MuiButton-icon": {
              color: IgCoreTextColors.interactive.neutral.disabled,
            },
            variants: [
              {
                props: { color: "link" },
                style: {
                  color: IgCoreTextColors.interactive.accent.default,
                  backgroundColor: "transparent",
                  ":hover": {
                    color: IgCoreTextColors.interactive.accent.hover,
                    backgroundColor: "transparent",
                  },
                  ":disabled": {
                    color: IgCoreTextColors.interactive.accent.disabled,
                    backgroundColor: "transparent",
                  },
                  ":focused": {
                    color: IgCoreTextColors.interactive.accent.focused,
                    backgroundColor: "transparent",
                  },
                },
              },
              {
                props: { color: "dark" },
                style: {
                  color: IgCoreTextColors.dark,
                  backgroundColor:
                    IgCoreBackgroundColors.interactive.dark.default,
                  ":hover": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.dark.hover,
                  },
                  ":disabled": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.dark.disabled,
                  },
                  ":focused": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.dark.focused,
                  },
                },
              },
              {
                props: { variant: "contained" },
                style: {
                  // Icons follow the filled button's text color; otherwise the
                  // app-wide `.lucide` grey lands on the dark fill.
                  "& .MuiButton-startIcon svg, & .MuiButton-endIcon svg": {
                    color: "inherit",
                  },
                },
              },
              {
                props: { color: "primary" },
                style: {
                  color: IgCoreTextColors.dark,
                  backgroundColor:
                    IgCoreBackgroundColors.interactive.accent.default,
                  ":hover": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.accent.hover,
                  },
                  // The label stays on-dark text over the disabled fill, as
                  // the design system's disabled primary button draws it —
                  // MUI's default disabled text grey reads as a washed-out
                  // label on the light blue.
                  ":disabled": {
                    // color: IgCoreTextColors.interactive.dark.default,
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.accent.disabled,
                  },
                  ":focused": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.accent.focused,
                  },
                },
              },
              {
                props: { color: "secondary" },
                style: {
                  color: IgCoreTextColors.primary,
                  backgroundColor:
                    IgCoreBackgroundColors.interactive.neutral.default,
                  ":hover": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.neutral.hover,
                  },
                  ":disabled": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.neutral.disabled,
                  },
                  ":focused": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.neutral.focused,
                    color: IgCoreTextColors.secondary,
                  },
                },
              },
              {
                props: { color: "warning" },
                style: {
                  // Warning surfaces are light amber, so they take dark text.
                  color: IgCoreTextColors.primary,
                  backgroundColor:
                    IgCoreBackgroundColors.interactive.warning.default,
                  ":hover": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.warning.hover,
                  },
                  ":disabled": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.warning.disabled,
                  },
                  ":focused": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.warning.focused,
                  },
                },
              },
              {
                props: { color: "error" },
                style: {
                  color: IgCoreTextColors.dark,
                  backgroundColor:
                    IgCoreBackgroundColors.interactive.error.default,
                  ":hover": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.error.hover,
                  },
                  ":disabled": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.error.disabled,
                  },
                  ":focused": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.error.focused,
                  },
                },
              },
              {
                props: { color: "upgrade" },
                style: {
                  color: IgCoreTextColors.dark,
                  backgroundColor: IgPurplePrimitives["800"],
                  ":hover": {
                    backgroundColor: IgPurplePrimitives["A100"],
                  },
                  ":disabled": {
                    backgroundColor: IgPurplePrimitives["300"],
                  },
                  ":focused": {
                    backgroundColor: IgPurplePrimitives["A200"],
                  },
                },
              },
              {
                props: { variant: "outlined" },
                style: {
                  color: IgCoreTextColors.primary,
                  borderColor: IgBorderColors.core.default,
                  backgroundColor: "transparent",
                  padding: "6.5px 15px",
                  ":hover": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.neutral.hover,
                    borderColor: IgBorderColors.core.hover,
                  },
                  ":disabled": {
                    backgroundColor: "transparent",
                  },
                  ":focused": {
                    backgroundColor: "transparent",
                  },
                },
              },
              {
                props: { variant: "text" },
                style: {
                  color: IgCoreTextColors.primary,
                  backgroundColor: "transparent",
                  padding: "7.5px 12px",
                  ":hover": {
                    backgroundColor:
                      IgCoreBackgroundColors.interactive.neutral.hover,
                  },
                  ":disabled": {
                    backgroundColor: "transparent",
                  },
                  ":focused": {
                    backgroundColor: "transparent",
                  },
                },
              },
              // Listed after the text variant so a text-style link keeps the
              // link colours; otherwise the text variant's primary ink wins.
              {
                props: { variant: "text", color: "link" },
                style: {
                  color: IgCoreTextColors.interactive.accent.default,
                  ":hover": {
                    color: IgCoreTextColors.interactive.accent.hover,
                    backgroundColor: "transparent",
                  },
                  ":disabled": {
                    color: IgCoreTextColors.interactive.accent.disabled,
                  },
                },
              },
              {
                props: { size: "large" },
                style: { minHeight: "40px", paddingBlock: "9.5px" },
              },
              {
                props: { size: "large", variant: "outlined" },
                style: { paddingBlock: "8.5px" },
              },
              // Listed after the variants so the 32px size keeps its own
              // padding and its shorter label line over theirs.
              {
                props: { size: "extraSmall" },
                style: {
                  minHeight: "32px !important",
                  padding: "7.5px 12px",
                  lineHeight: "16.8px",
                },
              },
              {
                props: { size: "extraSmall", variant: "outlined" },
                style: { padding: "6.5px 11px" },
              },
              {
                props: { size: "mini" },
                style: {
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: 500,
                  height: "26px",
                  lineHeight: "18px",
                  minHeight: "26px",
                  minWidth: "auto",
                  padding: "6px 16px",
                  whiteSpace: "nowrap",
                  "& .MuiButton-startIcon": {
                    marginLeft: 0,
                    marginRight: "6px",
                  },
                },
              },
              {
                // Iridescent gradient-framed "AI" affordance. The frame is a
                // white interior (padding-box) over the stroke gradient
                // (border-box); hover washes a faint surface tint under the
                // interior and sweeps a one-shot white shine across the button.
                // Self-contained: it neutralizes the default `color: primary`
                // background so the gradient frame reads on its own.
                props: { variant: "ai" },
                style: {
                  position: "relative",
                  overflow: "hidden",
                  minHeight: "32px",
                  minWidth: "fit-content",
                  border: "1px solid transparent",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  color: IgCoreTextColors.primary,
                  fontSize: "12px",
                  fontWeight: 600,
                  lineHeight: "16px",
                  letterSpacing: "0px",
                  backgroundColor: "transparent",
                  backgroundImage: `linear-gradient(#fff, #fff), ${AI_STROKE_GRADIENT}`,
                  backgroundOrigin: "border-box",
                  backgroundClip: "padding-box, border-box",
                  "&:hover, &:active, &:focus": {
                    backgroundColor: "transparent",
                    // White base under the wash keeps the border-box gradient
                    // from bleeding into the interior, so the fill reads as the
                    // surface wash over white rather than the full gradient.
                    // Border ring still shows the gradient (border-box).
                    backgroundImage: `${aiSurfaceWash(
                      0.08
                    )}, linear-gradient(#fff, #fff), ${AI_STROKE_GRADIENT}`,
                    backgroundClip: "padding-box, padding-box, border-box",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "transparent",
                    backgroundImage: "none",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    borderRadius: "inherit",
                    background: AI_SHINE_BAND,
                    transform: "translateX(-100%)",
                    pointerEvents: "none",
                  },
                  "&:hover::after, &:focus::after": {
                    animation: `${AI_SHINE_SWEEP} 1s ease-out`,
                  },
                },
              },
            ],
          },
        },
        defaultProps: {
          color: "primary",
          variant: "contained",
          disableElevation: true,
          size: "small",
        },
      },
      MuiSwitch: {
        defaultProps: {
          color: "primary",
        },
        styleOverrides: {
          root: {
            width: 36,
            height: 19,
            padding: 0,
            "& .MuiSwitch-switchBase": {
              padding: 0,
              margin: 2,
              transitionDuration: "300ms",
              "&.Mui-checked": {
                transform: "translateX(16px)",
                "& + .MuiSwitch-track": {
                  opacity: 1,
                  border: 0,
                },
                "&.Mui-disabled + .MuiSwitch-track": {
                  opacity: 0.5,
                },
              },
              "&.Mui-disabled + .MuiSwitch-track": {
                opacity: 0.7,
              },
            },
            "& .MuiSwitch-thumb": {
              boxSizing: "border-box",
              width: 15,
              height: 15,
            },
            "& .MuiSwitch-track": {
              borderRadius: 26 / 2,
              opacity: 1,
            },
            variants: [
              {
                props: { color: "plugin" },
                style: {
                  "& .MuiSwitch-switchBase": {
                    "&.Mui-checked": {
                      color: IgBorderColors.warning.default,
                      "& + .MuiSwitch-track": {
                        backgroundColor:
                          IgInteractiveBackgroundColors.accent.default,
                        boxShadow: `inset 0 0 0 2px ${IgBorderColors.warning.default}`,
                      },
                    },
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: "transparent",
                    boxShadow: `inset 0 0 0 2px white`,
                  },
                },
              },
              {
                props: { color: "primary" },
                style: {
                  ":hover": {
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor:
                        IgInteractiveBackgroundColors.accent.hover,
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: IgGrayPrimitives["A200"],
                    },
                  },
                  ":disabled": {
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor:
                        IgInteractiveBackgroundColors.accent.disabled,
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor:
                        IgInteractiveBackgroundColors.dark.disabled,
                    },
                  },
                  ":active": {
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor:
                        IgInteractiveBackgroundColors.accent.active,
                      boxShadow: `inset 0 0 0 2px ${IgInteractiveBackgroundColors.dark.active}`,
                    },
                    "& .MuiSwitch-track": {
                      boxShadow: `inset 0 0 0 2px ${IgInteractiveBackgroundColors.dark.active}`,
                      backgroundColor: IgGrayPrimitives["A200"],
                    },
                  },
                  ":focused": {
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor:
                        IgInteractiveBackgroundColors.accent.focused,
                      boxShadow: `inset 0 0 0 2px ${IgBorderColors.warning.default}`,
                    },
                    "& .MuiSwitch-track": {
                      boxShadow: `inset 0 0 0 2px ${IgBorderColors.accent.active}`,
                      backgroundColor: IgGrayPrimitives["A200"],
                    },
                  },
                  "& .MuiSwitch-switchBase": {
                    "&.Mui-checked": {
                      color: IgCoreBackgroundColors.baseLightHigh,
                      "& + .MuiSwitch-track": {
                        backgroundColor:
                          IgInteractiveBackgroundColors.accent.default,
                      },
                    },
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: IgGrayPrimitives["A100"],
                  },
                },
              },
              {
                props: { color: "secondary" },
                style: {
                  ":hover": {
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor:
                        IgInteractiveBackgroundColors.dark.disabled,
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor:
                        IgInteractiveBackgroundColors.neutral.hover,
                    },
                  },
                  ":disabled": {
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor:
                        IgInteractiveBackgroundColors.dark.disabled,
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor:
                        IgInteractiveBackgroundColors.dark.disabled,
                    },
                  },
                  ":active": {
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor:
                        IgInteractiveBackgroundColors.dark.active,
                      boxShadow: `inset 0 0 0 2px ${IgInteractiveBackgroundColors.dark.default}`,
                    },
                    "& .MuiSwitch-track": {
                      boxShadow: `inset 0 0 0 2px ${IgInteractiveBackgroundColors.dark.default}`,
                      backgroundColor:
                        IgInteractiveBackgroundColors.dark.active,
                    },
                  },
                  ":focused": {
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor:
                        IgInteractiveBackgroundColors.dark.focused,
                      boxShadow: `inset 0 0 0 2px ${IgBorderColors.core.default}`,
                    },
                    "& .MuiSwitch-track": {
                      boxShadow: `inset 0 0 0 2px ${IgBorderColors.core.default}`,
                      backgroundColor:
                        IgInteractiveBackgroundColors.dark.focused,
                    },
                  },
                  "& .MuiSwitch-switchBase": {
                    "&.Mui-checked": {
                      color: IgInteractiveBackgroundColors.dark.default,
                      "& + .MuiSwitch-track": {
                        backgroundColor:
                          IgInteractiveBackgroundColors.neutral.default,
                      },
                    },
                    color: IgInteractiveBackgroundColors.dark.default,
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor:
                      IgInteractiveBackgroundColors.dark.disabled,
                  },
                },
              },
            ],
          },
        },
      },
    },
  });
  return {
    ...baseTheme,
    typography: {
      ...baseTheme.typography,
      body3: {
        fontSize: "10px",
        lineHeight: "15px",
      },
      display: {
        fontSize: "32px",
        lineHeight: "40px",
      },
      code: {
        fontSize: "14px",
        lineHeight: "20px",
        fontFamily: IgFontFamily.code,
      },
    },
    palette: {
      ...baseTheme.palette,
      text: {
        ...baseTheme.palette.text,
        // MUI's defaults (rgba black at .87 / .6) render #212121 / #666, not
        // Text/Core/Primary and Secondary — every `text.*` colour must be a token.
        primary: IgCoreTextColors.primary,
        secondary: IgCoreTextColors.secondary,
        tertiary: IgCoreTextColors.tertiary,
        placeholder: IgCoreTextColors.placeholder,
        // MUI's .38 black; the design system has no separate disabled ink.
        disabled: IgCoreTextColors.placeholder,
        dark: IgCoreTextColors.dark,
        accent: IgCoreTextColors.accent,
        tonal: IgCoreTextColors.tonal,
        interactive: IgCoreTextColors.interactive,
      },
      accent: {
        main: IgCoreBackgroundColors.interactive.accent.default,
        dark: IgCoreBackgroundColors.interactive.accent.focused,
        light: IgCoreBackgroundColors.interactive.accent.disabled,
        contrastText: IgCoreTextColors.dark,
      },
      link: {
        main: IgCoreBackgroundColors.interactive.accent.default,
        dark: IgCoreBackgroundColors.interactive.accent.focused,
        light: IgCoreBackgroundColors.interactive.accent.disabled,
        contrastText: IgCoreTextColors.dark,
      },
      purple: {
        main: IgPurplePrimitives[100],
        dark: IgPurplePrimitives[200],
        light: IgPurplePrimitives[50],
        contrastText: IgCoreTextColors.primary,
      },
    },
    paper: {
      borderRadius: "8px",
    },
    pageContent: {
      maxWidth: "1450px",
    },
    page: {
      maxWidth: "1600px",
    },
    igShadows: IgShadows,
    igBorders: IgBorderColors,
    igIconColors: IgIconColors,
    igBackgroundColors: IgCoreBackgroundColors,
    igAi: {
      gradient: {
        icon: AI_ICON_GRADIENT,
        iconStops: AI_ICON_GRADIENT_STOPS,
        stroke: AI_STROKE_GRADIENT,
        dark: AI_DARK_GRADIENT,
      },
      surface: {
        subtle: aiSurfaceWash(0.05), // IgPaper variant="ai" interior tint
        soft: aiSurfaceWash(0.08), // AI button (`variant="ai"`) hover fill
        strong: aiSurfaceWash(0.1), // IgAiIcon round / square-soft surface (composited over white)
        badge: aiSurfaceWash(0.1, 203), // IgAiBadge label backdrop
      },
      shine: {
        band: AI_SHINE_BAND,
        sweep: AI_SHINE_SWEEP,
      },
    },
    igPrimitives: {
      gray: IgGrayPrimitives,
      green: IgGreenPrimitives,
      orange: IgOrangePrimitives,
      red: IgRedPrimitives,
      azure: IgAzurePrimitives,
      base: IgBasePrimitives,
      blue: IgBluePrimitives,
      lime: IgLimePrimitives,
      purple: IgPurplePrimitives,
      violet: IgVioletPrimitives,
      slate: IgSlatePrimitives,
      indigo: IgIndigoPrimitives,
      pink: IgPinkPrimitives,
      rose: IgRosePrimitives,
      amber: IgAmberPrimitives,
      yellow: IgYellowPrimitives,
      teal: IgTealPrimitives,
      cyan: IgCyanPrimitives,
      coral: IgCoralPrimitives,
    },
  };
}

interface LibraryThemeProviderProps {
  children?: ReactNode;
  theme: Theme;
}

export const IgTheme = createCustomTheme();

export const IntelligemsThemeProvider: FC<LibraryThemeProviderProps> = ({
  children,
  theme,
}) => <ThemeProvider theme={theme}>{children}</ThemeProvider>;

type SxProp = SxProps<typeof IgTheme>;
export type Styles = Record<string, SxProp>;
